import { Alert, Box, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { ComplianceTypes } from '@/constants';
import { EpisodeDetails } from '@/containers';
import { useDelayedSignupPrompt } from '@/hooks';
import { AnalyticsService, EVENT_CONSTANTS } from '@/services';
import {
  selectEpisodeCompliance,
  useAppSelector,
  useFetchEpisodeQuery,
} from '@/store';
import { appStyles } from '@/theme';

import { EpisodeSkeleton, WarningBanners } from './components';

export const Episode = () => {
  const { episodeId } = useParams();
  const { state } = useLocation();
  const referrer = state?.referrer;
  const compliance = useAppSelector(state =>
    selectEpisodeCompliance(state, episodeId),
  );

  useDelayedSignupPrompt(episodeId);

  // TODO useFetchEpisodequery causes unecessary
  // fetching waterfalls in EpisodeDetails
  const {
    data: episode,
    isLoading,
    isUninitialized,
  } = useFetchEpisodeQuery(
    { episodeId: episodeId!, compliance, includeArchived: true },
    { skip: !episodeId },
  );

  // Try to fetch non-region episode if not found
  const { data: fallbackEpisode, isLoading: isFallbackLoading } =
    useFetchEpisodeQuery(
      {
        episodeId: episodeId!,
        compliance:
          compliance === ComplianceTypes.CPD
            ? ComplianceTypes.CPE
            : ComplianceTypes.CPD,
        includeArchived: true,
      },
      { skip: isUninitialized || isLoading || !!episode || !episodeId },
    );

  useEffect(() => {
    const targetEpisode = episode || fallbackEpisode;
    const targetReferrer = referrer || {
      page: EVENT_CONSTANTS.PAGE.EPISODE_DETAILS,
      details: EVENT_CONSTANTS.AUTO_LOAD,
    };

    if (targetEpisode) {
      AnalyticsService.pageViewEpisode({
        episode: targetEpisode,
        referrer: targetReferrer,
      });
    }
  }, [episode, fallbackEpisode, referrer]);

  if (isLoading || isUninitialized || isFallbackLoading) {
    return (
      <Box sx={appStyles.limitPageWidth}>
        <EpisodeSkeleton />
      </Box>
    );
  }

  if (episode) {
    return (
      <Box sx={appStyles.limitPageWidth}>
        <WarningBanners episode={episode} />
        <EpisodeDetails episode={episode} />
      </Box>
    );
  }

  if (fallbackEpisode) {
    return (
      <Box sx={appStyles.limitPageWidth}>
        <WarningBanners episode={fallbackEpisode} isEpisodeNotNasba={true} />
        <EpisodeDetails episode={fallbackEpisode} />
      </Box>
    );
  }

  return (
    <Alert severity="error">
      <Typography variant="body3">
        Sorry, the episode you were looking for was not found.
      </Typography>
    </Alert>
  );
};
